import { useCallback, useEffect, useRef, useState } from "react";

function useWindowWidth() {
  const [w, setW] = useState(window.innerWidth);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
}
import { useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../auth/msalConfig";
import { DetectionCard, type Detection } from "../components/DetectionCard";
import { DetectionFocusView } from "../components/DetectionFocusView";
import { useAppContext } from "../context/AppContext";
import { theme, tint } from "../theme";

const API_BASE      = import.meta.env.VITE_API_BASE_URL as string;
const PAGE_SIZE     = 50;
// Focus (one large detection + filmstrip, the default) vs. the classic grid.
// Persisted so the choice survives a reload; absent/invalid → "focus".
const VIEW_MODE_KEY = "detectionsViewMode";
const MONTH_NAMES   = ["January","February","March","April","May","June",
                       "July","August","September","October","November","December"];

// ── Date helpers ──────────────────────────────────────────────────────────────
// Use local calendar date everywhere so the page reflects the user's clock,
// not UTC.  API queries convert local midnight → UTC before sending.

function toLocalDateStr(d: Date): string {
  const y  = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${da}`;
}

// Selected day survives a reload: read it from the ?date=YYYY-MM-DD query param,
// falling back to today. setSelectedDate mirrors it back into the URL, so a
// refresh (or a shared link) reopens the same day instead of jumping to today.
function initialSelectedDate(): string {
  const p = new URLSearchParams(window.location.search).get("date");
  return p && /^\d{4}-\d{2}-\d{2}$/.test(p) ? p : toLocalDateStr(new Date());
}

/** UTC bounds of a local calendar day, as the API expects them. */
function dayBounds(date: string): { startDt: string; endDt: string } {
  return {
    startDt: new Date(`${date}T00:00:00`).toISOString(),
    endDt:   new Date(`${date}T23:59:59`).toISOString(),
  };
}

function daysInMonth(ym: string): string[] {
  const [y, m] = ym.split("-").map(Number);
  const count  = new Date(Date.UTC(y, m, 0)).getUTCDate();
  return Array.from({ length: count }, (_, i) =>
    `${y}-${String(m).padStart(2,"0")}-${String(i + 1).padStart(2,"0")}`
  );
}

function shiftMonth(ym: string, delta: 1 | -1): string {
  const [y, m] = ym.split("-").map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth()+1).padStart(2,"0")}`;
}

// ── Category helpers ──────────────────────────────────────────────────────────

// A detection is not one category OR the other — it can carry both, and the
// current live example does: a missing sign (disappearance) together with a
// person without a helmet (safety). Asking "is this a safety event" forced a
// single answer and made such a card vanish under the Disappearance filter even
// though it genuinely contains one. Ask what it CONTAINS instead.
const isHelmetEvent = (name: string) => name.toLowerCase().includes("helmet");
const hasSafety = (d: Detection) =>
  d.detection_events?.some((e) => isHelmetEvent(e.type_name)) ?? false;
const hasDisappearance = (d: Detection) =>
  d.detection_events?.some((e) => !isHelmetEvent(e.type_name)) ?? false;

// Every label gets its OWN colour, matching the hue its marker is drawn in on
// the images — that is what makes the chip row double as the legend for the
// cards. The previous version returned one of two colours (amber for anything
// helmet-ish, red for everything else), so four different labels shared two
// colours and the row said nothing about which box was which.
//
// The known classes take the design's assignments; anything new is hashed onto
// the same palette so it gets a stable colour instead of falling back to red.
const LABEL_COLORS: Record<string, string> = {
  "person":                theme.info,
  "person with helmet":    theme.success,
  "person without helmet": theme.danger,
  "helmet":                theme.warning,
  "sign":                  theme.danger,
  "warning_sign":          theme.danger,
  "warning sign":          theme.danger,
  "traffic cone":          theme.warning,
  "fire extinguisher":     theme.success,
  "fire_extinguisher":     theme.success,
  "box":                   theme.info,
  "pallet":                theme.info,
  "jackhammer":            theme.goldText,
};
const LABEL_PALETTE = [theme.danger, theme.warning, theme.success, theme.info, theme.goldText];

function labelColor(label: string): string {
  const known = LABEL_COLORS[label.trim().toLowerCase()];
  if (known) return known;
  // Stable across reloads: the same label must not change colour between the
  // chip and the marker, or between one visit and the next.
  let hash = 0;
  for (const ch of label) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0;
  return LABEL_PALETTE[hash % LABEL_PALETTE.length];
}

function tickColor(detection_events: { type_name: string }[]) {
  return detection_events.some((e) => e.type_name.toLowerCase().includes("helmet"))
    ? theme.warning
    : theme.danger;
}

// ── Lightweight timeline tick type (matches /api/detections/timeline) ─────────

interface TimelineTick {
  id:               number;
  timestamp:        string;
  detection_events: { type_name: string }[];
}

// ── MonthStrip ────────────────────────────────────────────────────────────────

interface MonthStripProps {
  viewMonth:     string;             // "YYYY-MM"
  selectedDate:  string;             // "YYYY-MM-DD"
  eventDates:    Map<string, number>; // date → count
  onSelectDate:  (d: string) => void;
  onPrevMonth:   () => void;
  onNextMonth:   () => void;
}

const STRIP_BREAKPOINT = 640; // px — above this: single row, below: two rows

function MonthStrip({ viewMonth, selectedDate, eventDates, onSelectDate, onPrevMonth, onNextMonth }: MonthStripProps) {
  const [y, m] = viewMonth.split("-").map(Number);
  const days    = daysInMonth(viewMonth);
  const today   = toLocalDateStr(new Date());
  const isWide  = useWindowWidth() >= STRIP_BREAKPOINT;

  // Auto-scroll selected day into view whenever it changes or the month changes
  const daysRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!daysRef.current) return;
    const sel = daysRef.current.querySelector<HTMLElement>("[data-selected='true']");
    sel?.scrollIntoView({ block: "nearest", inline: "center", behavior: "smooth" });
  }, [selectedDate, viewMonth]);

  const navBtn: React.CSSProperties = {
    background: "transparent",
    border: "none",
    color: theme.textSecondary,
    cursor: "pointer",
    padding: "4px 10px",
    fontSize: 18,
    lineHeight: 1,
    flexShrink: 0,
    borderRadius: theme.radius,
    transition: "color 0.15s",
  };

  const monthLabel = (
    <span style={{
      fontSize: 13,
      fontWeight: 600,
      color: theme.textSecondary,
      whiteSpace: "nowrap",
      minWidth: 110,
      textAlign: "center",
      letterSpacing: "0.03em",
    }}>
      {MONTH_NAMES[m - 1]} {y}
    </span>
  );

  // Shared scrollable day cells strip
  const dayStrip = (
    <div
      ref={daysRef}
      className="no-scrollbar"
      style={{
        display: "flex",
        alignItems: "stretch",
        flex: 1,
        overflowX: "auto",
        scrollbarWidth: "none",
        height: "100%",
      }}
    >
        {days.map((dateStr) => {
          const dayNum     = parseInt(dateStr.slice(8), 10);
          const isToday    = dateStr === today;
          const isSelected = dateStr === selectedDate;
          const hasEvents  = (eventDates.get(dateStr) ?? 0) > 0;

          let color: string = theme.textMuted;
          let bg: string    = "transparent";
          let fw: React.CSSProperties["fontWeight"] = 400;

          if (isSelected) {
            color = theme.onDark;
            bg    = theme.gold;
            fw    = 700;
          } else if (hasEvents) {
            color = theme.danger;
            bg    = tint(theme.danger, 8);
            fw    = 600;
          } else if (isToday) {
            color = theme.textSecondary;
            fw    = 600;
          }

          return (
            <button
              key={dateStr}
              data-selected={isSelected ? "true" : undefined}
              onClick={() => onSelectDate(dateStr)}
              title={dateStr}
              style={{
                flex: "1 0 32px",
                background: bg,
                border: "none",
                // Inset so adjacent highlighted days stay visibly separate. Run
                // edge to edge they merge into one continuous band and stop
                // reading as individual, clickable days.
                margin: "5px 1.5px",
                borderRadius: 5,
                color,
                fontWeight: fw,
                fontSize: 12,
                cursor: "pointer",
                padding: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 2,
                position: "relative",
                transition: "background 0.1s, color 0.1s",
              }}
            >
              {dayNum}
              {isToday && !isSelected && (
                <span style={{
                  width: 4, height: 4,
                  borderRadius: "50%",
                  background: theme.gold,
                  position: "absolute",
                  bottom: 3,
                }} />
              )}
            </button>
          );
        })}
    </div>
  );

  // ── Wide: single row ─────────────────────────────────────────────────────────
  if (isWide) {
    return (
      <div style={{
        display: "flex",
        alignItems: "center",
        background: theme.bgCard,
        borderBottom: `1px solid ${theme.border}`,
        height: 46,
        padding: "0 8px",
        gap: 4,
        userSelect: "none",
      }}>
        <button style={navBtn} onClick={onPrevMonth} title="Previous month">‹</button>
        {monthLabel}
        <button style={navBtn} onClick={onNextMonth} title="Next month">›</button>
        <div style={{ width: 1, height: 24, background: theme.border, flexShrink: 0, margin: "0 4px" }} />
        {dayStrip}
      </div>
    );
  }

  // ── Narrow: two rows ─────────────────────────────────────────────────────────
  return (
    <div style={{
      background: theme.bgCard,
      borderBottom: `1px solid ${theme.border}`,
      userSelect: "none",
    }}>
      <div style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        height: 36,
        gap: 4,
        borderBottom: `1px solid ${theme.border}`,
      }}>
        <button style={navBtn} onClick={onPrevMonth} title="Previous month">‹</button>
        {monthLabel}
        <button style={navBtn} onClick={onNextMonth} title="Next month">›</button>
      </div>
      <div style={{ height: 36 }}>
        {dayStrip}
      </div>
    </div>
  );
}

// ── DayTimeline ───────────────────────────────────────────────────────────────

interface DayTimelineProps {
  allDayTicks:    TimelineTick[];
  currentPageIds: Set<number>;
  selectedDate:   string;
}

function DayTimeline({ allDayTicks, currentPageIds, selectedDate }: DayTimelineProps) {
  const H = 38; // total height px
  const TICK_H = 22;

  const todayStr = toLocalDateStr(new Date());
  const isToday  = selectedDate === todayStr;
  const nowPct   = isToday
    ? (new Date().getHours() + new Date().getMinutes() / 60) / 24 * 100
    : null;

  return (
    <div style={{
      position: "relative",
      height: H,
      // A deeper surface than the day strip above, so the panel reads as two
      // stacked halves rather than one flat block. The panel draws the outer
      // edge, so the band needs no border of its own.
      background: theme.bgInput,
      borderTop: `1px solid ${theme.border}`,
      overflow: "hidden",
    }}>
      {/* Hour guide lines + labels */}
      {[0, 6, 12, 18, 24].map((h) => (
        <div key={h} style={{
          position: "absolute",
          left: `${h / 24 * 100}%`,
          top: 0,
          bottom: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-end",
          pointerEvents: "none",
        }}>
          {h > 0 && h < 24 && (
            <div style={{
              position: "absolute",
              top: 0, bottom: 0,
              left: 0,
              borderLeft: `1px dashed ${theme.border}`,
            }} />
          )}
          <span style={{
            position: "absolute",
            bottom: 3,
            left: h === 0 ? 4 : h === 24 ? undefined : 3,
            right: h === 24 ? 4 : undefined,
            fontSize: 10,
            color: theme.textMuted,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            lineHeight: 1,
          }}>
            {h}h
          </span>
        </div>
      ))}

      {/* Current-time line */}
      {nowPct !== null && (
        <div style={{
          position: "absolute",
          left: `${nowPct}%`,
          top: 0,
          bottom: 0,
          width: 1.5,
          background: theme.gold,
          opacity: 0.7,
          pointerEvents: "none",
        }} />
      )}

      {/* Event ticks — all day; dimmed when not on current page */}
      {allDayTicks.map((tick) => {
        const dt      = new Date(tick.timestamp);
        const hours   = dt.getHours() + dt.getMinutes() / 60 + dt.getSeconds() / 3600;
        const pct     = hours / 24 * 100;
        const color   = tickColor(tick.detection_events);
        const hhmm    = `${String(dt.getHours()).padStart(2,"0")}:${String(dt.getMinutes()).padStart(2,"0")}`;
        const onPage  = currentPageIds.has(tick.id);

        return (
          <div
            key={tick.id}
            title={`${hhmm} – ${tick.detection_events.map((e) => e.type_name).join(", ")}${onPage ? "" : " (not on this page)"}`}
            style={{
              position: "absolute",
              left: `${pct}%`,
              top: "50%",
              transform: "translate(-50%, -50%)",
              width: onPage ? 4 : 3,
              height: onPage ? TICK_H : TICK_H * 0.6,
              background: color,
              borderRadius: 2,
              opacity: onPage ? 0.85 : 0.22,
              pointerEvents: "auto",
              cursor: "default",
            }}
          />
        );
      })}
    </div>
  );
}

// ── LabelChip ─────────────────────────────────────────────────────────────────

interface LabelChipProps {
  label:    string;
  checked:  boolean;
  color:    string;
  onToggle: () => void;
}

function LabelChip({ label, checked, color, onToggle }: LabelChipProps) {
  return (
    <button
      onClick={onToggle}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        // Fully rounded, with the label's own colour as a dot rather than a
        // checkbox. These are a legend as much as a control — the dot is the
        // same colour the marker gets on the image, so the chip row doubles as
        // the key for what is drawn on the cards.
        padding: "4px 11px",
        borderRadius: 12,
        border: `1px solid ${checked ? tint(color, 40) : theme.border}`,
        background: checked ? tint(color, 6) : theme.bgCard,
        color: checked ? theme.textPrimary : theme.textMuted,
        fontFamily: theme.fontCondensed,
        fontSize: theme.fontSize2Xs, fontWeight: 500,
        letterSpacing: "0.02em",
        cursor: "pointer", userSelect: "none",
        transition: "background 0.15s, border-color 0.15s, color 0.15s",
      }}
    >
      <span style={{
        width: 7, height: 7, borderRadius: "50%", flexShrink: 0,
        background: checked ? color : tint(theme.textMuted, 45),
        transition: "background 0.15s",
      }} />
      {label}
    </button>
  );
}

/** A labelled on/off switch, per the design's control for the two hidden-by-
 *  default categories. A switch rather than a button because these do not
 *  perform an action — they change what the page contains, and a switch is the
 *  control that reads as a persistent state rather than a thing you press. */
function ToggleSwitch({ label, checked, onToggle, title }: {
  label: string; checked: boolean; onToggle: () => void; title?: string;
}) {
  return (
    <label
      title={title}
      style={{
        display: "inline-flex", alignItems: "center", gap: 8,
        cursor: "pointer", flexShrink: 0, userSelect: "none",
      }}
    >
      <span style={{ fontSize: theme.fontSize2Xs, color: theme.textSecondary }}>{label}</span>
      <button
        role="switch"
        aria-checked={checked}
        aria-label={label}
        onClick={onToggle}
        style={{
          width: 36, height: 20, flexShrink: 0,
          borderRadius: 10, border: "none", padding: 0, cursor: "pointer",
          background: checked ? theme.gold : tint(theme.textMuted, 35),
          transition: "background 0.18s",
          position: "relative",
        }}
      >
        <span style={{
          position: "absolute", top: 2, left: checked ? 18 : 2,
          width: 16, height: 16, borderRadius: "50%",
          background: theme.bgCard,
          boxShadow: "0 1px 3px rgba(0,0,0,0.3)",
          transition: "left 0.18s",
        }} />
      </button>
    </label>
  );
}


// ── DetectionsPage ────────────────────────────────────────────────────────────

type ViewMode = "grid" | "focus";

function initialViewMode(): ViewMode {
  try {
    return localStorage.getItem(VIEW_MODE_KEY) === "grid" ? "grid" : "focus";
  } catch {
    return "grid";   // localStorage may be unavailable (private mode / sandbox)
  }
}

interface NewDetectionMessage     { type: "new_detection";      data: Detection }
interface DetectionUpdatedMessage { type: "detection_updated";   data: Detection }
interface EvalUpdatedMessage      { type: "evaluation_updated";  data: Detection }
interface CentralLabelUpdatedMessage { type: "central_label_updated"; data: Detection }

export function DetectionsPage() {
  const { instance, accounts } = useMsal();
  const { deviceId, lastMessage } = useAppContext();
  const navigate = useNavigate();

  // ── Calendar state ──────────────────────────────────────────────────────────

  // todayStr is dynamic: updates at UTC midnight and on tab focus after sleep.
  const [todayStr, setTodayStr] = useState(() => toLocalDateStr(new Date()));
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [viewMonth, setViewMonth]       = useState(() => initialSelectedDate().slice(0, 7));
  const [eventDates, setEventDates]     = useState<Map<string, number>>(new Map());

  // Refresh todayStr at the next local midnight (re-arms itself each day).
  useEffect(() => {
    const now  = new Date();
    const next = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
    const timer = setTimeout(
      () => setTodayStr(toLocalDateStr(new Date())),
      next.getTime() - now.getTime() + 200,   // +200 ms buffer
    );
    return () => clearTimeout(timer);
  }, [todayStr]); // re-arms after each rollover

  // Also refresh when the tab becomes visible again (device woke from sleep).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible")
        setTodayStr(toLocalDateStr(new Date()));
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // If the user was viewing "today" and the day rolled over, advance the view.
  const selectedDateRef = useRef(selectedDate);
  useEffect(() => { selectedDateRef.current = selectedDate; }, [selectedDate]);

  // Keep ?date= in sync with the selected day so a reload reopens the same day.
  useEffect(() => {
    const url = new URL(window.location.href);
    if (url.searchParams.get("date") !== selectedDate) {
      url.searchParams.set("date", selectedDate);
      window.history.replaceState(null, "", url);
    }
  }, [selectedDate]);
  const prevTodayRef = useRef(todayStr);
  useEffect(() => {
    const prev = prevTodayRef.current;
    prevTodayRef.current = todayStr;
    if (todayStr === prev) return;                      // no actual day change
    if (selectedDateRef.current !== prev) return;       // user chose a different day
    // User was on "today" — advance to the new day.
    setSelectedDate(todayStr);
    setViewMonth(todayStr.slice(0, 7));
  }, [todayStr]);

  // ── Timeline ticks (all events for the day, lightweight) ───────────────────
  const [allDayTicks, setAllDayTicks] = useState<TimelineTick[]>([]);

  // ── Detection list state ────────────────────────────────────────────────────
  const [detections, setDetections]         = useState<Detection[]>([]);
  const [page, setPage]                     = useState(1);
  const [totalCount, setTotalCount]         = useState(0);
  const [availableLabels, setAvailableLabels] = useState<string[]>([]);
  const [loading, setLoading]               = useState(false);
  const [error, setError]                   = useState<string | null>(null);
  const [showMarkers, setShowMarkers]       = useState(true);
  const [viewMode, setViewMode]             = useState<ViewMode>(initialViewMode);
  // One selection rather than two independent toggles: the old pair could be
  // switched off together, which rendered an empty page with nothing on screen
  // explaining why. A segmented control cannot reach that state.
  const [category, setCategory] = useState<"all" | "disappearance" | "safety">("all");
  // Central-ML false positives are hidden by default; the user can opt in.
  const [showMlFalse, setShowMlFalse]       = useState(false);
  // Near-duplicates likewise: a repeat shows a scene already reviewed, so it is
  // noise in the default view but must stay reachable and countable.
  const [showDuplicates, setShowDuplicates] = useState(false);
  const [showTooDark, setShowTooDark]       = useState(false);
  const [stats, setStats] = useState<{ real: number; ml_false_alarms: number; duplicates: number; too_dark: number } | null>(null);
  const [selectedLabels, setSelectedLabels] = useState<Set<string> | null>(null);
  // Annotation-QA push
  const [cvatCount, setCvatCount]           = useState(0);
  const [cvatEnabled, setCvatEnabled]       = useState(false);
  const [pushingCvat, setPushingCvat]       = useState(false);

  // ── Derived ─────────────────────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const effectiveSelected = selectedLabels ?? new Set(availableLabels);
  const allSelected       = selectedLabels === null || effectiveSelected.size === availableLabels.length;

  // Label + ML-false filtering happen on the backend; the category toggle is
  // client-side.  The ml_verdict guard is a defensive second line in case a
  // hidden false positive slipped into the local list via a WS message.
  const visibleDetections = detections.filter((d) => {
    const tooDark = d.duplicate_reason === "too_dark";
    const duplicate = Boolean(d.duplicate_reason) && !tooDark;
    return (
    (category === "all" ||
      (category === "safety" ? hasSafety(d) : hasDisappearance(d))) &&
    // Mirrors the server's precedence: a row that is both a central false alarm
    // and a repeat is governed by the ML-false toggle, because that is the
    // bucket the header counts it in.
    (showMlFalse || d.ml_verdict !== "false_alarm") &&
    (showDuplicates || !duplicate || d.ml_verdict === "false_alarm") &&
    (showTooDark || !tooDark || d.ml_verdict === "false_alarm")
    );
  });

  // ── Token helper ────────────────────────────────────────────────────────────
  const fetchToken = useCallback(async () => {
    const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
    return r.accessToken;
  }, [instance, accounts]);

  // ── Fetch calendar (which days have events in the viewed month) ─────────────
  // The backend returns raw UTC timestamps; we group by local date here so the
  // dots on the calendar match the user's clock regardless of timezone.
  const fetchCalendar = useCallback(async (month: string) => {
    try {
      const token  = await fetchToken();
      const serial = deviceId ? `&device_serial=${encodeURIComponent(deviceId)}` : "";
      const resp   = await fetch(
        `${API_BASE}/api/detections/calendar?month=${month}${serial}` +
          `&include_ml_false_alarms=${showMlFalse}` +
          `&include_duplicates=${showDuplicates}` +
          `&include_too_dark=${showTooDark}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) return;
      const timestamps = await resp.json() as string[];
      const prefix = `${month}-`;          // e.g. "2026-04-"
      const counts = new Map<string, number>();
      for (const ts of timestamps) {
        const localDate = toLocalDateStr(new Date(ts));
        if (localDate.startsWith(prefix))  // discard the ±1-day buffer padding
          counts.set(localDate, (counts.get(localDate) ?? 0) + 1);
      }
      setEventDates(counts);
    } catch { /* silent */ }
  }, [fetchToken, deviceId, showMlFalse, showDuplicates, showTooDark]);

  // ── Fetch the day's real / false-positive / duplicate split ────────────────
  // Server-side, and deliberately NOT derived from the loaded page: the list is
  // paginated and filtered by the toggles, so a client-side tally would describe
  // whatever happens to be on screen rather than the day.
  const fetchStats = useCallback(async () => {
    try {
      const token = await fetchToken();
      const { startDt, endDt } = dayBounds(selectedDate);
      const serial = deviceId ? `&device_serial=${encodeURIComponent(deviceId)}` : "";
      const resp = await fetch(
        `${API_BASE}/api/detections/stats?start_date=${encodeURIComponent(startDt)}` +
        `&end_date=${encodeURIComponent(endDt)}${serial}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) return;
      setStats(await resp.json());
    } catch { /* silent — the line just stays blank */ }
  }, [fetchToken, deviceId, selectedDate]);

  // ── Fetch how many false detections can be sent for review (+ enabled flag) ──
  const fetchCvatCount = useCallback(async () => {
    try {
      const token  = await fetchToken();
      const serial = deviceId ? `?device_serial=${encodeURIComponent(deviceId)}` : "";
      const resp   = await fetch(
        `${API_BASE}/api/detections/cvat/count${serial}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) return;
      const data = await resp.json() as { count: number; enabled: boolean };
      setCvatCount(data.count);
      setCvatEnabled(data.enabled);
    } catch { /* silent */ }
  }, [fetchToken, deviceId]);

  // ── Fetch distinct labels for the selected day ─────────────────────────────
  const fetchLabels = useCallback(async () => {
    try {
      const token   = await fetchToken();
      const serial  = deviceId ? `&device_serial=${encodeURIComponent(deviceId)}` : "";
      const { startDt, endDt } = dayBounds(selectedDate);
      const resp = await fetch(
        `${API_BASE}/api/detections/labels` +
        `?start_date=${encodeURIComponent(startDt)}&end_date=${encodeURIComponent(endDt)}${serial}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) return;
      setAvailableLabels(await resp.json() as string[]);
    } catch { /* silent */ }
  }, [fetchToken, deviceId, selectedDate]);

  // ── Fetch all timeline ticks for the selected day (no pagination) ──────────
  const fetchTimeline = useCallback(async () => {
    try {
      const token   = await fetchToken();
      const serial  = deviceId ? `&device_serial=${encodeURIComponent(deviceId)}` : "";
      const { startDt, endDt } = dayBounds(selectedDate);
      const resp = await fetch(
        `${API_BASE}/api/detections/timeline` +
        `?start_date=${encodeURIComponent(startDt)}&end_date=${encodeURIComponent(endDt)}${serial}` +
        `&include_ml_false_alarms=${showMlFalse}` +
          `&include_duplicates=${showDuplicates}` +
          `&include_too_dark=${showTooDark}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) return;
      setAllDayTicks(await resp.json() as TimelineTick[]);
    } catch { /* silent */ }
  }, [fetchToken, deviceId, selectedDate, showMlFalse, showDuplicates, showTooDark]);

  // ── Fetch one page of detections (replaces current list) ───────────────────
  // Pass `labelsOverride` when calling right after a label state change so the
  // fetch uses the new value before React re-renders with the updated state.
  const fetchPage = useCallback(async (
    pageNum: number,
    labelsOverride?: Set<string> | null,
  ) => {
    setLoading(true);
    setError(null);
    try {
      const token   = await fetchToken();
      const serial  = deviceId ? `&device_serial=${encodeURIComponent(deviceId)}` : "";
      const { startDt, endDt } = dayBounds(selectedDate);
      const currentOffset = (pageNum - 1) * PAGE_SIZE;

      // Use explicit override if provided, otherwise fall back to current state.
      const activeLabels = labelsOverride !== undefined ? labelsOverride : selectedLabels;

      // null = "no label filter applied" (show everything). An EMPTY set is the
      // opposite: the user unticked every label, so nothing matches. Those two
      // were collapsed into the same "send no detection_types param" branch,
      // which made unticking every chip show the full list — the exact inverse
      // of what it says. There is no query that asks the API for nothing, so
      // answer it here instead of round-tripping.
      if (activeLabels && activeLabels.size === 0) {
        setDetections([]);
        setTotalCount(0);
        setLoading(false);
        return;
      }

      const typeParams = activeLabels
        ? [...activeLabels].map((t) => `&detection_types=${encodeURIComponent(t)}`).join("")
        : "";

      const resp = await fetch(
        `${API_BASE}/api/detections?limit=${PAGE_SIZE}&offset=${currentOffset}` +
        `&start_date=${encodeURIComponent(startDt)}&end_date=${encodeURIComponent(endDt)}` +
        `&include_ml_false_alarms=${showMlFalse}` +
        `&include_duplicates=${showDuplicates}` +
        `&include_too_dark=${showTooDark}` +
        `${serial}${typeParams}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json() as { items: Detection[]; total: number };
      setDetections(data.items);
      setTotalCount(data.total);
      setPage(pageNum);
    } catch (err) {
      setError(String(err));
    } finally {
      setLoading(false);
    }
  }, [fetchToken, deviceId, selectedDate, selectedLabels, showMlFalse, showDuplicates, showTooDark]);

  const goToPage = useCallback((pageNum: number) => {
    if (pageNum < 1 || loading) return;
    fetchPage(pageNum);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [fetchPage, loading]);

  // ── Send pending false detections for review ──────────────────────────────
  const handlePushCvat = useCallback(async () => {
    if (pushingCvat || cvatCount === 0) return;
    setPushingCvat(true);
    try {
      const token  = await fetchToken();
      const serial = deviceId ? `?device_serial=${encodeURIComponent(deviceId)}` : "";
      const resp   = await fetch(
        `${API_BASE}/api/detections/cvat/push${serial}`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      if (!resp.ok) {
        // Messages are for the operator, not the developer: say what is
        // happening and what to do, keep the status code as a small hint.
        if (resp.status === 404) {
          setError("Nothing is flagged for relabeling.");
        } else if (resp.status === 502) {
          setError("Some of the flagged images could not be read from storage. Try again in a moment; if it keeps failing, contact an administrator.");
        } else {
          setError(`Sending for relabeling failed. Please try again; if it keeps failing, contact an administrator (error ${resp.status}).`);
        }
        return;
      }
      const data = await resp.json() as { task_id: number | null; task_url: string | null };
      setCvatCount(0);
      fetchPage(page);
      // Straight into the in-app review editor — no extra steps.
      if (data.task_id != null) navigate(`/review/${data.task_id}`);
    } catch (err) {
      setError(String(err));
    } finally {
      setPushingCvat(false);
    }
  }, [pushingCvat, cvatCount, fetchToken, deviceId, fetchPage, page, navigate]);

  // When device or selected date changes → reset labels + page + timeline
  useEffect(() => {
    setDetections([]);
    setPage(1);
    setTotalCount(0);
    setError(null);
    setSelectedLabels(null);
    setAllDayTicks([]);
    fetchLabels();
    fetchTimeline();
    fetchPage(1, null);
  }, [deviceId, selectedDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // When viewMonth or device changes → reload calendar
  useEffect(() => {
    fetchCalendar(viewMonth);
  }, [viewMonth, deviceId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Toggling the ML-false filter re-queries the current day (skip first render).
  const mlFirstRef = useRef(true);
  useEffect(() => {
    if (mlFirstRef.current) { mlFirstRef.current = false; return; }
    fetchPage(1);
    fetchTimeline();
    fetchCalendar(viewMonth);   // red day-dots honour the AI-false-positives toggle too
  }, [showMlFalse, showDuplicates, showTooDark]); // eslint-disable-line react-hooks/exhaustive-deps

  // Refresh the review queue and the day's split when the device changes or
  // detections are mutated (eval flipped, deletion, etc.).
  useEffect(() => {
    fetchCvatCount();
    fetchStats();
  }, [deviceId, detections, fetchCvatCount, fetchStats]);

  // Azure VM power transitions and review-service boot take longer than one request.
  // Keep the lifecycle state current while the operator waits, without
  // repeatedly reloading the rest of the detections page.
  useEffect(() => {
    const timer = window.setInterval(fetchCvatCount, 20_000);
    return () => window.clearInterval(timer);
  }, [fetchCvatCount]);

  // Persist the grid/focus choice so a reload reopens the same presentation.
  useEffect(() => {
    try { localStorage.setItem(VIEW_MODE_KEY, viewMode); } catch { /* ignore */ }
  }, [viewMode]);

  // Realtime WS messages
  useEffect(() => {
    if (!lastMessage) return;
    const msg = lastMessage as Partial<NewDetectionMessage | DetectionUpdatedMessage | EvalUpdatedMessage | CentralLabelUpdatedMessage>;

    if (msg.type === "new_detection" && msg.data) {
      const d = msg.data!;
      // Respect the ML-false filter: don't surface hidden false positives live.
      // Refresh the header's split before any filtering decision: a suppressed
      // duplicate never enters `detections`, so the effect that watches that
      // list would never fire and the count would stay stale for precisely the
      // rows the filter is hiding.
      fetchStats();

      if (d.ml_verdict === "false_alarm" && !showMlFalse) return;
      // Same precedence as the server: a row that is both is governed by the
      // ML-false toggle, already handled above.
      const tooDark = d.duplicate_reason === "too_dark";
      if (tooDark && d.ml_verdict !== "false_alarm" && !showTooDark) return;
      if (d.duplicate_reason && !tooDark && d.ml_verdict !== "false_alarm" && !showDuplicates) return;
      if (!deviceId || !d.device_serial || d.device_serial === deviceId) {
        const dDateStr = toLocalDateStr(new Date(d.timestamp));
        if (dDateStr === selectedDate) {
          setDetections((prev) => {
            const exists = prev.some((x) => x.id === d.id);
            if (exists) return prev.map((x) => x.id === d.id ? d : x);
            // Only prepend on page 1 — other pages show a fixed offset window
            return page === 1 ? [d, ...prev] : prev;
          });
          setSelectedLabels((prev) => {
            if (prev === null) return null;
            const newTypes = (d.detection_events ?? [])
              .map((e) => e.type_name)
              .filter((t) => !prev.has(t));
            if (!newTypes.length) return prev;
            return new Set([...prev, ...newTypes]);
          });
        }
        // Calendar dot — only increment for genuinely new detections
        setEventDates((prev) => {
          const next = new Map(prev);
          next.set(dDateStr, (next.get(dDateStr) ?? 0) + 1);
          return next;
        });
        // Add to all-day timeline ticks if on the selected day
        if (dDateStr === selectedDate) {
          setAllDayTicks((prev) => {
            if (prev.some((t) => t.id === d.id)) return prev;
            const tick: TimelineTick = {
              id:               d.id,
              timestamp:        typeof d.timestamp === "string" ? d.timestamp : new Date(d.timestamp as unknown as string).toISOString(),
              detection_events: d.detection_events ?? [],
            };
            return [...prev, tick].sort(
              (a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
            );
          });
        }
      }
    }

    // Images arrived for an existing detection — update card silently, no toast
    if (msg.type === "detection_updated" && msg.data) {
      const d = msg.data as Detection;
      setDetections((prev) => prev.map((x) => x.id === d.id ? d : x));
    }

    if (msg.type === "evaluation_updated" && msg.data)
      setDetections((prev) => prev.map((d) => d.id === msg.data!.id ? msg.data! : d));

    // Central-model pre-labeling finished — update the card in place.
    if (msg.type === "central_label_updated" && msg.data)
      setDetections((prev) => prev.map((d) => d.id === msg.data!.id ? msg.data! : d));
  }, [lastMessage, deviceId, selectedDate, showMlFalse, showDuplicates, showTooDark]);

  const handleEvaluationChange = (id: number, evaluation: string | null) =>
    setDetections((prev) => prev.map((d) => d.id === id ? { ...d, evaluation } : d));

  const handleDelete = (id: number) =>
    setDetections((prev) => prev.filter((d) => d.id !== id));

  // ── Label filter handlers ───────────────────────────────────────────────────
  const toggleAll = () => {
    const newLabels = allSelected ? new Set<string>() : null;
    setSelectedLabels(newLabels);
    fetchPage(1, newLabels);
  };

  const toggleLabel = (label: string) => {
    const current   = selectedLabels ?? new Set(availableLabels);
    const next      = new Set(current);
    next.has(label) ? next.delete(label) : next.add(label);
    const newLabels = next.size === availableLabels.length ? null : next;
    setSelectedLabels(newLabels);
    fetchPage(1, newLabels);
  };

  // ── Shared toggle-button style ──────────────────────────────────────────────
  const filterBtn = (active: boolean, color: string): React.CSSProperties => ({
    display: "flex", alignItems: "center", gap: 7,
    padding: "5px 13px",
    borderRadius: theme.radius,
    // The design fills both states rather than tinting one and leaving the
    // other transparent: active is a solid soft-gold chip with the darker gold
    // as text, inactive is white with primary text. A faint tint over the page
    // ground and a muted grey label was legible but read as disabled — these
    // are live controls and both states have to look it.
    border: `1px solid ${active ? tint(color, 55) : theme.border}`,
    background: active ? theme.goldSoft : theme.bgCard,
    color: active ? theme.goldText : theme.textPrimary,
    fontSize: theme.fontSizeXs, fontWeight: 600, cursor: "pointer",
    transition: "background 0.15s, border-color 0.15s, color 0.15s",
    letterSpacing: "0.01em",
  });

  // The one action this page affords: hand the frames an operator flagged as
  // mislabelled to a human annotator. Defined here rather than inline so the
  // header row stays readable — its disabled/label states carry four cases.
  // The count is FRAMES, not detections, because that is what gets sent.
  const sendForReviewButton = (
    <button
      onClick={handlePushCvat}
      disabled={pushingCvat || cvatCount === 0}
      title={
        cvatCount === 0
          ? "No images flagged as mislabelled yet — use \"Click to fix labels\" on a frame"
          : `Send ${cvatCount} flagged image${cvatCount === 1 ? "" : "s"} for manual relabeling`
      }
      style={{
        display: "flex", alignItems: "center", gap: 7,
        padding: "8px 16px", borderRadius: 5, border: "none",
        background: theme.gold,
        color: theme.onGold,
        fontSize: theme.fontSizeXs, fontWeight: 700, fontFamily: "inherit",
        letterSpacing: "0.02em",
        cursor: pushingCvat || cvatCount === 0 ? "not-allowed" : "pointer",
        opacity: pushingCvat || cvatCount === 0 ? 0.5 : 1,
        transition: "opacity 0.15s",
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 17v2a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/>
        <polyline points="7 9 12 4 17 9"/>
        <line x1="12" y1="4" x2="12" y2="16"/>
      </svg>
      {pushingCvat
        ? "Sending…"
        : `Send for manual relabeling${cvatCount > 0 ? ` (${cvatCount})` : ""}`}
    </button>
  );

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div>

      {/* ── Month strip ───────────────────────────────────────────────────── */}
      {/* Month strip and day timeline read as one control — pick a day above,
          see that day's events below — so they share a single rounded panel
          rather than running full-bleed as two separate bands butted against
          the navigation rail. The inset also stops the first day cell from
          sitting flush against the rail's border. */}
      <div style={{
        margin: "18px 28px 0",
        border: `1px solid ${theme.border}`,
        borderRadius: 8,
        overflow: "hidden",
      }}>
      <MonthStrip
        viewMonth={viewMonth}
        selectedDate={selectedDate}
        eventDates={eventDates}
        onSelectDate={(d) => {
          setSelectedDate(d);
          setViewMonth(d.slice(0, 7));
        }}
        onPrevMonth={() => setViewMonth((m) => shiftMonth(m, -1))}
        onNextMonth={() => setViewMonth((m) => shiftMonth(m, 1))}
      />

      {/* ── Day timeline ──────────────────────────────────────────────────── */}
      <DayTimeline
        allDayTicks={allDayTicks}
        currentPageIds={new Set(detections.map((d) => d.id))}
        selectedDate={selectedDate}
      />
      </div>

      {/* Loading is otherwise invisible: clicking a filter re-queries the
          server, and until it answers the page shows the PREVIOUS result — so
          nothing on screen says "wait", and a filter that legitimately returns
          the same rows looks like a control that did not work. A hairline along
          the content's top edge is enough to say the page is busy without
          moving anything or blocking a click. */}
      <div style={{ position: "relative", height: 2, overflow: "hidden" }}>
        {loading && (
          <div
            role="progressbar"
            aria-label="Loading detections"
            style={{
              position: "absolute", inset: 0,
              background: tint(theme.gold, 22),
            }}
          >
            <div style={{
              position: "absolute", top: 0, bottom: 0, width: "35%",
              background: theme.gold,
              animation: "indeterminate 1.1s ease-in-out infinite",
            }} />
          </div>
        )}
      </div>

      {/* ── Padded content ────────────────────────────────────────────────── */}
      <div style={{ padding: "26px 28px 28px" }}>

        {/* ── Header: what the day contains, and the one action it affords ──── */}
        {/* The page title is gone: the rail already says Detections, and the
            day's split is the more useful thing to put in the most prominent
            row. The date lives on the month strip directly above. */}
        <div>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            flexWrap: "wrap", gap: 12, marginBottom: 14,
          }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
              {/* Nothing at all until the counts arrive. The old date line was
                  standing in for them, so the header briefly wore the previous
                  design and then swapped layouts under the reader — a flash of
                  stale furniture is worse than a moment of empty space, and the
                  date is already on the month strip directly above. */}
              {stats && (
                <span style={{
                  display: "inline-flex", alignItems: "baseline", gap: 10,
                  flexWrap: "wrap", fontSize: theme.fontSizeSm,
                }}>
                  {([
                    [stats.real,            "real event"],
                    [stats.ml_false_alarms, "edge false-positive"],
                    [stats.duplicates,      "edge duplicate"],
                    [stats.too_dark,        "too dark image"],
                  ] as const).map(([n, noun], i) => (
                    <span key={noun} style={{ whiteSpace: "nowrap" }}>
                      {i > 0 && <span style={{ color: theme.borderStrong, marginRight: 8 }}>·</span>}
                      <span style={{ fontWeight: 700, color: theme.textPrimary }}>{n}</span>
                      <span style={{ color: theme.textSecondary }}> {noun}{n === 1 ? "" : "s"}</span>
                    </span>
                  ))}
                </span>
              )}
              {/* Pagination — only once there is something to page through.
                  Rendering it during the fetch put a lone "…" where the counts
                  belong, so the row assembled itself in pieces; the hairline
                  above already says the page is busy. */}
              {totalCount > 0 && (
                <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                  <button
                    onClick={() => goToPage(page - 1)}
                    disabled={page <= 1 || loading}
                    title="Previous page"
                    style={{
                      background: "transparent",
                      border: `1px solid ${page > 1 && !loading ? theme.border : "transparent"}`,
                      color: page > 1 && !loading ? theme.textSecondary : theme.textMuted,
                      borderRadius: theme.radius,
                      width: 24, height: 24,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: page > 1 && !loading ? "pointer" : "default",
                      fontSize: 14, lineHeight: 1, padding: 0,
                      opacity: page <= 1 || loading ? 0.35 : 1,
                      transition: "opacity 0.15s, border-color 0.15s",
                    }}
                  >‹</button>
                  <span style={{
                    color: theme.textMuted, fontSize: 13,
                    minWidth: 52, textAlign: "center",
                  }}>
                    {loading ? "…" : `${page} / ${totalPages}`}
                  </span>
                  <button
                    onClick={() => goToPage(page + 1)}
                    disabled={page >= totalPages || loading}
                    title="Next page"
                    style={{
                      background: "transparent",
                      border: `1px solid ${page < totalPages && !loading ? theme.border : "transparent"}`,
                      color: page < totalPages && !loading ? theme.textSecondary : theme.textMuted,
                      borderRadius: theme.radius,
                      width: 24, height: 24,
                      display: "flex", alignItems: "center", justifyContent: "center",
                      cursor: page < totalPages && !loading ? "pointer" : "default",
                      fontSize: 14, lineHeight: 1, padding: 0,
                      opacity: page >= totalPages || loading ? 0.35 : 1,
                      transition: "opacity 0.15s, border-color 0.15s",
                    }}
                  >›</button>
                  <span style={{ color: theme.textMuted, fontSize: 13, marginLeft: 2 }}>
                    · {totalCount} event{totalCount !== 1 ? "s" : ""}
                  </span>
                </div>
              )}
            </div>

            {/* The only action the header affords: everything else on this page
                is filtering or presentation, which lives in the row below. */}
            {cvatEnabled && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                {sendForReviewButton}
              </div>
            )}
          </div>

          {/* ── Filters and presentation ────────────────────────────────────── */}
          <div style={{
            display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap",
            marginBottom: 18, paddingBottom: 14, borderBottom: `1px solid ${theme.border}`,
          }}>
            {/* Category: one choice, so "nothing shown" is unreachable */}
            <div style={{
              display: "inline-flex", border: `1px solid ${theme.border}`,
              borderRadius: 5, overflow: "hidden", flexShrink: 0,
            }}>
              {([
                ["all",           "All",           theme.gold],
                ["disappearance", "Disappearance", theme.danger],
                ["safety",        "Safety",        theme.warning],
              ] as const).map(([key, label, color], i) => {
                const active = category === key;
                return (
                  <button
                    key={key}
                    onClick={() => setCategory(key)}
                    style={{
                      padding: "6px 13px",
                      fontSize: theme.fontSizeXs, fontWeight: active ? 700 : 500,
                      fontFamily: "inherit",
                      border: "none", cursor: "pointer",
                      borderLeft: i > 0 ? `1px solid ${theme.border}` : "none",
                      // Solid fills in both states, as the design draws them:
                      // the selected segment is a soft-gold chip with the
                      // darker gold as text, the rest are white with primary
                      // text. Transparent segments over the page ground and a
                      // muted grey label read as disabled.
                      background: active
                        ? (key === "all" ? theme.goldSoft : tint(color, 16))
                        : theme.bgCard,
                      color: active
                        ? (key === "all" ? theme.goldText : color)
                        : theme.textPrimary,
                      transition: "background 0.15s, color 0.15s",
                    }}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {availableLabels.length > 0 && (
              <>
                <div style={{ width: 1, height: 22, background: theme.border, flexShrink: 0 }} />
                <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                  <LabelChip label="All" checked={allSelected} color={theme.gold} onToggle={toggleAll} />
                  {availableLabels.map((label) => (
                    <LabelChip
                      key={label}
                      label={label}
                      checked={effectiveSelected.has(label)}
                      color={labelColor(label)}
                      onToggle={() => toggleLabel(label)}
                    />
                  ))}
                </div>
              </>
            )}

            <div style={{ width: 1, height: 22, background: theme.border, flexShrink: 0 }} />

            {/* Switches, not buttons: these do not perform an action, they
                change what the page contains. A switch reads as a persistent
                state; a button reads as something you press and that finishes. */}
            {/* "Edge", not "AI": both名 describe something the EDGE model on the
                router got wrong, which the central model or the hash filter then
                caught. Calling them "AI" said nothing about which of the two
                models is being judged. */}
            <ToggleSwitch
              label="Edge false-positives"
              checked={showMlFalse}
              onToggle={() => setShowMlFalse((v) => !v)}
              title={"Detections the edge model reported but the central model found no anomaly in. " +
                     "Hidden by default because there is nothing left to decide about them."}
            />
            <ToggleSwitch
              label="Edge duplicates"
              checked={showDuplicates}
              onToggle={() => setShowDuplicates((v) => !v)}
              title={"Repeats: either the edge device's own before and after frames show the same scene, " +
                     "or it re-sent the scene it sent last time. No central evaluation runs on them."}
            />
            <ToggleSwitch
              label="Too dark"
              checked={showTooDark}
              onToggle={() => setShowTooDark((v) => !v)}
              title="Detections with at least one image below the reviewable-brightness threshold. No central evaluation runs on them."
            />

            {/* Presentation, not filtering — pushed right so it reads as a
                different class of control from everything left of it. */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginLeft: "auto" }}>
              <button onClick={() => setShowMarkers((v) => !v)}
                title={showMarkers ? "Hide markers" : "Show markers"}
                style={filterBtn(showMarkers, theme.gold)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {showMarkers
                    ? <><circle cx="12" cy="12" r="4"/><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/></>
                    : <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></>
                  }
                </svg>
                Markers
              </button>

              {/* Grid ⇄ focus presentation toggle (same detections, either way) */}
              <button onClick={() => setViewMode((v) => v === "focus" ? "grid" : "focus")}
                title={viewMode === "focus" ? "Back to the grid view" : "Focus one detection at a time"}
                style={filterBtn(viewMode === "focus", theme.gold)}
              >
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {viewMode === "focus"
                    ? <><rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/><rect x="14" y="14" width="7" height="7" rx="1"/></>
                    : <><rect x="3" y="4" width="18" height="12" rx="1"/><rect x="3" y="19" width="4" height="2" rx="0.5"/><rect x="10" y="19" width="4" height="2" rx="0.5"/><rect x="17" y="19" width="4" height="2" rx="0.5"/></>
                  }
                </svg>
                Focus view
              </button>
            </div>
          </div>
        </div>

        {error && <p style={{ color: theme.danger, marginBottom: 16, fontSize: 14 }}>Error: {error}</p>}

        {viewMode === "focus" ? (
          <DetectionFocusView
            detections={visibleDetections}
            showMarkers={showMarkers}
            onEvaluationChange={handleEvaluationChange}
            onDelete={handleDelete}
          />
        ) : (
          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))",
            gap: 16,
          }}>
            {visibleDetections.map((d) => (
              <DetectionCard
                key={d.id}
                detection={d}
                showMarkers={showMarkers}
                onEvaluationChange={handleEvaluationChange}
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}

        <div style={{ height: 48, marginTop: 8, display: "flex", alignItems: "center", justifyContent: "center", gap: 12 }}>
          {loading && <span style={{ color: theme.textMuted, fontSize: 13 }}>Loading…</span>}
          {!loading && detections.length === 0 && !error && (
            <span style={{ color: theme.textMuted, fontSize: 13 }}>No detection events on this day</span>
          )}
          {!loading && totalPages > 1 && (
            <>
              <button
                onClick={() => goToPage(page - 1)}
                disabled={page <= 1}
                style={{
                  ...filterBtn(false, theme.textMuted),
                  opacity: page <= 1 ? 0.3 : 1,
                  cursor: page <= 1 ? "default" : "pointer",
                }}
              >‹ Prev</button>
              <span style={{ color: theme.textMuted, fontSize: 13 }}>{page} / {totalPages}</span>
              <button
                onClick={() => goToPage(page + 1)}
                disabled={page >= totalPages}
                style={{
                  ...filterBtn(false, theme.textMuted),
                  opacity: page >= totalPages ? 0.3 : 1,
                  cursor: page >= totalPages ? "default" : "pointer",
                }}
              >Next ›</button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
